import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { Media } from 'src/app/shared/enums/enums'
import {
  AdminPanelService,
  PaginationInfo,
  UserData,
  UserManagementResponse,
} from '../../service/admin/admin-panel.service'
import { DdcLoggerService } from '../../service/log/ddc-logger.service'
import { DdcCountryFlagComponent } from '../../shared/ddc/ddc-country-flag/ddc-country-flag.component'

@Component({
  selector: 'app-ddc-user-management',
  templateUrl: './ddc-user-management.component.html',
  styleUrls: ['./ddc-user-management.component.scss'],
  standalone: true,
  imports: [CommonModule, DdcCountryFlagComponent],
})
export class DdcUserManagementComponent implements OnInit, OnDestroy {
  users: UserData[] = []
  pagination: PaginationInfo | null = null
  isLoading = false
  error: string | null = null

  protected assets = Media.ASSETS

  private subscription = new Subscription()

  constructor(
    private adminPanelService: AdminPanelService,
    private logger: DdcLoggerService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadUsers()
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  loadUsers(page: number = 1): void {
    if (!this.adminPanelService.isAdminAuthenticated()) {
      this.router.navigate(['/admin'])
      return
    }

    this.isLoading = true
    this.error = null

    const sub = this.adminPanelService.getUsersData(page, 10).subscribe({
      next: (response: UserManagementResponse) => {
        this.users = response.users
        this.pagination = response.pagination
        this.isLoading = false
        this.logger.info('User management data loaded successfully')

        // Debug: Log users with profile pictures
        const usersWithPics = this.users.filter(u => u.has_profile_pic && u.profile_pic_url)
        if (usersWithPics.length > 0) {
          this.logger.info(`Found ${usersWithPics.length} users with profile pictures:`)
          usersWithPics.forEach(u => {
            this.logger.info(`User ${u.username}: ${u.profile_pic_url}`)
          })
        }
      },
      error: error => {
        this.error = 'Failed to load user data. Please try again.'
        this.isLoading = false
        this.logger.error('Error loading user data:', error)

        if (error.status === 401) {
          this.router.navigate(['/admin'])
        }
      },
    })

    this.subscription.add(sub)
  }

  goToNextPage(): void {
    if (this.pagination?.has_next && this.pagination.next_page) {
      this.loadUsers(this.pagination.next_page)
    }
  }

  goToPreviousPage(): void {
    if (this.pagination?.has_previous && this.pagination.previous_page) {
      this.loadUsers(this.pagination.previous_page)
    }
  }

  // Add this method to enable trackBy in the template
  trackByUserId(index: number, user: any): any {
    return user.id
  }
  // Add this method to your component class
  getPaginationEnd(): number {
    if (!this.pagination) return 0
    return Math.min(
      this.pagination.current_page * this.pagination.page_size,
      this.pagination.total_users,
    )
  }
  goToPage(page: number): void {
    if (page >= 1 && page <= (this.pagination?.total_pages || 1)) {
      this.loadUsers(page)
    }
  }

  refreshUsers(): void {
    const currentPage = this.pagination?.current_page || 1
    this.loadUsers(currentPage)
  }

  goBack(): void {
    this.router.navigate(['/admin'])
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A'

    try {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    } catch {
      return 'Invalid Date'
    }
  }

  getStatusBadgeClass(user: UserData): string {
    if (!user.is_active) return 'status-inactive'
    if (user.is_superuser) return 'status-superuser'
    if (user.is_staff) return 'status-staff'
    return 'status-active'
  }

  getStatusText(user: UserData): string {
    if (!user.is_active) return 'Inactive'
    if (user.is_superuser) return 'Superuser'
    if (user.is_staff) return 'Staff'
    return 'Active'
  }

  getSubscriptionBadgeClass(user: UserData): string {
    const subscription = user.subscription || 'Free Tier'
    switch (subscription) {
      case 'Plus':
        return 'subscription-plus'
      case 'Pro':
        return 'subscription-pro'
      case 'Free Tier':
      default:
        return 'subscription-free'
    }
  }

  getSubscriptionEndClass(user: UserData): string {
    if (!user.subscription_end) return 'subscription-date-na'

    const endDate = new Date(user.subscription_end)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (endDate < now) return 'subscription-expired'
    if (daysUntilExpiry <= 30) return 'subscription-expiring-soon'
    return 'subscription-active'
  }

  getPageNumbers(): number[] {
    if (!this.pagination) return []

    const totalPages = this.pagination.total_pages
    const currentPage = this.pagination.current_page
    const delta = 2 // Number of pages to show on each side of current page

    const range = []
    const rangeWithDots = []

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, -1) // -1 represents dots
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push(-1, totalPages) // -1 represents dots
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots.filter((page, index, arr) => arr.indexOf(page) === index)
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement
    imgElement.src =
      'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'
  }
}
